import { useState } from "react";
import { useAction } from "convex/react";
import { Loader2, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { isIframed } from "@/lib/iframe";
import { api } from "../../../convex/_generated/api";
import type { PremiumStatus } from "../OnboardingFlow";

const TOPUP_MESSAGE_TYPE = "sapps:show-topup";
const TOPUP_SOURCE = "bitetrack-onboarding";
const STANDALONE_TOPUP_URL = "https://sapps.co/organizations/settings";

export const RESTORE_INSUFFICIENT_COPY =
  "Still insufficient credits — top up above, or continue free with manual logging.";
export const RESTORE_CHECK_FAILED_COPY =
  "We couldn't check your credits right now — tap Restore purchases to try again.";

export type RestoreState = "idle" | "checking" | "insufficient" | "check_failed";
export type RestoreOutcome = "restored" | "insufficient" | "check_failed";

interface CreditCheckResult {
  readonly sufficient: boolean;
}

/**
 * Restore-purchase flow mirroring PaywallGate's credit-check pattern:
 * sufficient credits complete onboarding free, insufficient credits keep the
 * paywall visible with an inline message, and a failed check surfaces retry
 * copy without blocking the user (X / Not-now stay available).
 */
export async function runRestoreCheck(
  check: () => Promise<CreditCheckResult>,
  onChoose: (status: PremiumStatus, choice: string) => void,
): Promise<RestoreOutcome> {
  try {
    const result = await check();
    if (result.sufficient) {
      onChoose("free", "restored");
      return "restored";
    }
    return "insufficient";
  } catch {
    return "check_failed";
  }
}

interface PaywallScreenProps {
  readonly completing: boolean;
  readonly onChoose: (status: PremiumStatus, choice: string) => void;
  readonly initialRestoreState?: RestoreState;
}

export function PaywallScreen({
  completing,
  onChoose,
  initialRestoreState = "idle",
}: PaywallScreenProps) {
  const [topUpRequested, setTopUpRequested] = useState(false);
  const [restoreState, setRestoreState] = useState<RestoreState>(initialRestoreState);
  const checkAiCredits = useAction(api.ai.checkAiCredits);

  const handleRestore = () => {
    if (completing || restoreState === "checking") return;
    console.log("[Frontend][Paywall] Restore purchases requested");
    setRestoreState("checking");
    void runRestoreCheck(() => checkAiCredits({}), onChoose).then((outcome) => {
      if (outcome === "restored") {
        console.log("[Frontend][Paywall] Restore found sufficient credits, completing free");
        return;
      }
      if (outcome === "check_failed") {
        console.warn("[Frontend][Paywall] Restore credit check failed, showing retry copy");
      } else {
        console.log("[Frontend][Paywall] Restore found insufficient credits");
      }
      setRestoreState(outcome);
    });
  };

  const handleTopUp = () => {
    if (isIframed()) {
      console.log("[Frontend][Paywall] Top-up requested via parent postMessage", {
        type: TOPUP_MESSAGE_TYPE,
        source: TOPUP_SOURCE,
      });
      window.parent.postMessage({ type: TOPUP_MESSAGE_TYPE, source: TOPUP_SOURCE }, "*");
      setTopUpRequested(true);
    } else {
      console.log("[Frontend][Paywall] Top-up requested, opening standalone settings", {
        url: STANDALONE_TOPUP_URL,
      });
      window.open(STANDALONE_TOPUP_URL, "_blank", "noopener,noreferrer");
      setTopUpRequested(true);
    }
  };

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex justify-end py-2">
        <button
          type="button"
          aria-label="Close"
          onClick={() => onChoose("free", "dismissed")}
          disabled={completing}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-text-muted select-none transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80"
        >
          <X size={22} aria-hidden="true" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="flex max-h-[160px] min-h-0 flex-1 items-center justify-center">
          <img
            src="/onboarding/paywall.png"
            alt="BiteTrack AI features preview"
            className="max-h-full max-w-full rounded-3xl border border-border object-contain shadow-sm"
          />
        </div>
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold leading-tight text-text">
            You're out of AI credits
          </h1>
          <p className="text-sm leading-relaxed text-text-muted">
            BiteTrack's AI features — photo meal scans, smart estimates, and voice logging — run on
            Sapps AI credits, managed in the Sapps app. Top up to keep using AI, or continue free
            with manual logging.
          </p>
        </div>
        {topUpRequested && (
          <p role="status" className="ob-fade-swap text-sm text-text-muted">
            Top-up requested — finish in the Sapps app, then come back.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-3">
        <Button
          data-testid="paywall-topup-btn"
          variant="primary"
          size="lg"
          className="w-full"
          onClick={handleTopUp}
        >
          Top up credits
        </Button>
        <button
          type="button"
          data-testid="paywall-restore-btn"
          onClick={handleRestore}
          disabled={completing || restoreState === "checking"}
          aria-busy={restoreState === "checking"}
          className="inline-flex min-h-[44px] w-full select-none items-center justify-center gap-2 rounded-xl border border-border bg-surface text-sm font-semibold text-text transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80 disabled:opacity-50"
        >
          {restoreState === "checking" ? (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          ) : (
            <RotateCcw size={16} aria-hidden="true" />
          )}
          {restoreState === "checking" ? "Checking…" : "Restore purchases"}
        </button>
        {restoreState === "insufficient" && (
          <p
            role="status"
            data-testid="paywall-restore-insufficient"
            className="ob-fade-swap text-center text-sm text-warning"
          >
            {RESTORE_INSUFFICIENT_COPY}
          </p>
        )}
        {restoreState === "check_failed" && (
          <p
            role="alert"
            data-testid="paywall-restore-error"
            className="ob-fade-swap text-center text-sm text-danger"
          >
            {RESTORE_CHECK_FAILED_COPY}
          </p>
        )}
        <button
          type="button"
          data-testid="paywall-manual-btn"
          onClick={() => onChoose("free", "manual_logging")}
          disabled={completing}
          aria-busy={completing}
          className="inline-flex min-h-[44px] select-none items-center justify-center gap-2 text-sm font-medium text-text-muted transition-opacity active:opacity-80 disabled:opacity-50"
        >
          {completing && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          Not now — continue with manual logging (free)
        </button>
      </div>
    </div>
  );
}
